import React, { useRef, useEffect, useState } from 'react';
import { useFloating, autoUpdate, offset, shift, flip } from '@floating-ui/react';
import fr from './languages/fr.json';
import en from './languages/en.json';
import es from './languages/es.json';
import de from './languages/de.json';
import it from './languages/it.json';
import nl from './languages/nl.json';
import pt from './languages/pt.json';
import ru from './languages/ru.json';
import ar from './languages/ar.json';
import zh from './languages/zh.json';
import hi from './languages/hi.json';
import bn from './languages/bn.json';
import ja from './languages/ja.json';
import tr from './languages/tr.json';
import ko from './languages/ko.json';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MarkdownComponents from './MarkdownComponents';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/light';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import vs2015 from 'react-syntax-highlighter/dist/esm/styles/hljs/vs2015';

SyntaxHighlighter.registerLanguage('json', json);

function JsonBlockWithCopy({ jsonString }) {
  const [copied, setCopied] = useState(false);
  const [dragPos, setDragPos] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = window.localStorage.getItem('jsonCopyBtnPos');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {}
      }
    }
    return { x: 10, y: 10 };
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const [menu, setMenu] = useState({ visible: false, selection: '' });
  const codeBlockRef = useRef(null);

  // floating-ui setup
  const { x, y, refs, strategy, update } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(5), shift(), flip()],
    whileElementsMounted: autoUpdate,
  });

  // Get language from window.lang
  const lang = typeof window !== 'undefined' && window.localStorage ? (window.localStorage.getItem('lang') || 'en') : 'en';

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  // Drag logic
  const handleMouseDown = (e) => {
    setDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: dragPos.x,
      origY: dragPos.y
    };
    document.body.style.userSelect = 'none';
  };
  const handleMouseMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const newPos = {
      x: Math.max(0, dragRef.current.origX + dx),
      y: Math.max(0, dragRef.current.origY + dy)
    };
    setDragPos(newPos);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('jsonCopyBtnPos', JSON.stringify(newPos));
    }
  };
  const handleMouseUp = () => {
    setDragging(false);
    document.body.style.userSelect = '';
  };
  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  // Context menu logic
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    let selection = '';
    if (window.getSelection) {
      selection = window.getSelection().toString();
    }
    
    setMenu({ visible: true, selection });
    
    // Set virtual reference element at mouse position
    refs.setReference({
      getBoundingClientRect() {
        return {
          width: 0,
          height: 0,
          x: e.clientX,
          y: e.clientY,
          top: e.clientY,
          left: e.clientX,
          right: e.clientX,
          bottom: e.clientY,
        };
      },
    });
    
    // Force hide Chrome's context menu
    return false;
  };
  const handleMenuCopy = () => {
    if (menu.selection) {
      navigator.clipboard.writeText(menu.selection);
    } else {
      navigator.clipboard.writeText(jsonString);
    }
    setMenu({ ...menu, visible: false });
  };
  const handleMenuAsk = () => {
    // Custom event for VolcanAI
    window.dispatchEvent(new CustomEvent('volcanai-ask', { detail: { text: menu.selection } }));
    setMenu({ ...menu, visible: false });
  };
  const handleMenuClose = () => {
    setMenu({ ...menu, visible: false });
  };

  useEffect(() => {
    if (menu.visible) {
      window.addEventListener('click', handleMenuClose);
    } else {
      window.removeEventListener('click', handleMenuClose);
    }
    return () => {
      window.removeEventListener('click', handleMenuClose);
    };
  }, [menu.visible]);

  // Menu labels
  const labels = {
    copy: lang === 'fr' ? 'Copier' : 'Copy',
    copySelection: lang === 'fr' ? 'Copier la sélection' : 'Copy selection',
    ask: lang === 'fr' ? 'Demander à VolcanAI' : 'Ask VolcanAI'
  };

  return (
    <div style={{position:'relative', marginBottom: '16px'}}>
      <button
        onClick={handleCopy}
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          top: dragPos.y,
          left: dragPos.x,
          zIndex: 10,
          background: copied ? '#4CAF50' : '#222',
          color: '#fff',
          border: copied ? '2px solid #4CAF50' : '2px solid #444',
          borderRadius: 5,
          padding: '5px 10px',
          fontSize: 13,
          fontWeight: 'bold',
          cursor: dragging ? 'grabbing' : 'grab',
          transition: 'all 0.2s',
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          opacity: 0.95,
          userSelect: 'none'
        }}
        title={copied ? (lang === 'fr' ? 'Copié !' : 'Copied!') : (lang === 'fr' ? 'Copier le code JSON' : 'Copy JSON code')}
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{marginRight:2}} xmlns="http://www.w3.org/2000/svg">
          <rect x="7" y="3" width="10" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="3" y="7" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
        {copied ? (lang === 'fr' ? 'COPIÉ!' : 'COPIED!') : (lang === 'fr' ? 'COPIER' : 'COPY')}
      </button>
      <div
        ref={codeBlockRef}
        onContextMenu={handleContextMenu}
        onContextMenuCapture={handleContextMenu}
        style={{
          position:'relative',
          border: '2px solid #252525',
          borderRadius: 6,
          background: '#101010'
        }}
      >
        <SyntaxHighlighter
          style={vs2015}
          language="json"
          PreTag="div"
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          customStyle={{
            background: '#101010',
            color: '#fff',
            borderRadius: 6,
            border: 'none',
            padding: 18,
            fontSize: 15,
            fontFamily: 'Fira Mono, Menlo, Monaco, Consolas, monospace',
            position: 'relative',
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          {jsonString.replace(/\r\n/g, '\n').replace(/\r/g, '\n')}
        </SyntaxHighlighter>
        {menu.visible && (
          <div
            ref={refs.setFloating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              background: '#222',
              color: '#fff',
              borderRadius: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
              border: '1.5px solid #444',
              minWidth: 160,
              zIndex: 9999,
              padding: '8px 0',
              fontSize: 15
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding:'8px 18px',
                cursor:'pointer',
                fontWeight:500,
                transition: 'background 0.2s'
              }}
              onClick={handleMenuCopy}
              onMouseEnter={(e) => e.target.style.background = '#333'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              {menu.selection ? labels.copySelection : labels.copy}
            </div>
            {menu.selection && (
              <div
                style={{
                  padding:'8px 18px',
                  cursor:'pointer',
                  fontWeight:500,
                  transition: 'background 0.2s'
                }}
                onClick={handleMenuAsk}
                onMouseEnter={(e) => e.target.style.background = '#333'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                {labels.ask}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MessagesList({
  messages,
  copyFeedback,
  copyToClipboard,
  regenerateMessage,
  shareMessage,
  filePreviews,
  isLoading,
  lang: propLang
}) {

  const messagesEndRef = useRef(null);
  const [lang, setLang] = useState(() => {
    if (propLang) return propLang;
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem('lang') || 'en';
    }
    return 'en';
  });

  // Update lang when prop changes
  useEffect(() => {
    if (propLang && propLang !== lang) {
      setLang(propLang);
    }
  }, [propLang]);

  // Dictionnaire des langues
  const translations = { fr, en, es, de, it, nl, pt, ru, ar, zh, hi, bn, ja, tr, ko };

  // Fonction de traduction
  function t(key) {
    const dict = translations[lang] || {};
    // Supporte les clés imbriquées (ex: "welcome.subtitle")
    return key.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), dict) || key;
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Met à jour la fonction globale de traduction et la langue dans localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.lang = lang;
      window.localStorage && window.localStorage.setItem('lang', lang);
      // Définit la fonction globale t
      window.t = t;
    }
  }, [lang]);

  // Écouter les changements de langue depuis localStorage 
  useEffect(() => {
    const handleStorageChange = () => {
      const newLang = window.localStorage?.getItem('lang') || 'en';
      if (newLang !== lang) {
        setLang(newLang);
      }
    };
    
    // Vérifier les changements depuis d'autres onglets
    window.addEventListener('storage', handleStorageChange);
    
    // Vérifier les changements dans le même onglet
    const interval = setInterval(() => {
      const newLang = window.localStorage?.getItem('lang') || 'en';
      if (newLang !== lang) {
        setLang(newLang);
      }
    }, 100);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [lang]);

  return (
    <div className="messages-container" style={{paddingTop: '100px'}}>
      {messages.length === 0 ? (
        <div className="welcome">
          <h1 style={{fontSize:38,fontWeight:700,margin:'32px 0 18px 0',textAlign:'center'}}>VolcanAI</h1>
          <div style={{fontSize:22,fontWeight:400,opacity:0.85,marginTop:10,textAlign:'center'}}>{t('welcome.subtitle')}</div>
        </div>
      ) : (
        messages.map((message, index) => (
          <div key={message.id || index} className={`message ${message.role}`}>
            <div className="message-avatar">
              {message.role === 'user' ? (
                <div className="user-avatar">U</div>
              ) : (
                <div className="ai-avatar">AI</div>
              )}
              {copyFeedback[message.id] && (
                <div className="copy-feedback">
                  {copyFeedback[message.id]}
                </div>
              )}
            </div>
            <div className="message-content">
              {message.content && (
                message.role === 'assistant' ? (
                  (() => {
                    // Détection plus stricte du JSON - désactivée pour éviter les faux positifs
                    let isJson = false;
                    let jsonString = '';
                    
                    if (typeof message.content === 'string') {
                      // Nettoyer le contenu avant de tenter le parsing
                      const cleanedContent = message.content
                        .replace(/\r\n/g, '\n')
                        .replace(/\r/g, '\n')
                        .trim();
                        
                      // DÉSACTIVÉ temporairement - trop de faux positifs avec le streaming
                      // La détection JSON sera gérée uniquement par MarkdownComponents
                      isJson = false;
                      
                      /*             
                      // Vérifier si c'est vraiment du JSON complet avant de tenter le parsing
                      const looksLikeCompleteJson = (cleanedContent.startsWith('{') && cleanedContent.endsWith('}')) ||
                                                   (cleanedContent.startsWith('[') && cleanedContent.endsWith(']'));
                      
                      // Vérifications supplémentaires pour s'assurer que le JSON est vraiment complet
                      const hasIncompleteStrings = cleanedContent.match(/:\s*"[^"]*$/m);
                      const hasTrailingComma = cleanedContent.match(/,\s*$/m);
                      const hasIncompleteKey = cleanedContent.match(/"\w*"\s*:\s*$/m);
                      const hasOpenString = cleanedContent.match(/"\w*"\s*:\s*"[^"]*$/m);
                      
                      // Équilibrage des accolades/crochets
                      const openBraces = (cleanedContent.match(/\{/g) || []).length;
                      const closeBraces = (cleanedContent.match(/\}/g) || []).length;
                      const openBrackets = (cleanedContent.match(/\[/g) || []).length;
                      const closeBrackets = (cleanedContent.match(/\]/g) || []).length;
                      const isBalanced = (openBraces === closeBraces) && (openBrackets === closeBrackets);
                      
                      // Essayer de parser comme JSON seulement si tout semble vraiment complet
                      const isReallyComplete = looksLikeCompleteJson && isBalanced && 
                                              !hasIncompleteStrings && !hasTrailingComma && 
                                              !hasIncompleteKey && !hasOpenString;
                      
                      if (isReallyComplete) {
                        try {
                          const parsed = JSON.parse(cleanedContent);
                          jsonString = JSON.stringify(parsed, null, 2);
                          isJson = true;
                        } catch (e) {
                          isJson = false;
                        }
                      }
                      */
                      
                      // Si ce n'est pas du JSON pur, vérifier s'il y a des blocs JSON dans le markdown
                      if (!isJson) {
                        const jsonBlockRegex = /```json\s*([\s\S]*?)\s*```/gi;
                        const match = jsonBlockRegex.exec(cleanedContent);
                        if (match) {
                          try {
                            const parsed = JSON.parse(match[1].trim());
                            // Si on trouve du JSON valide dans un bloc de code, on garde le markdown original
                            isJson = false;
                          } catch (jsonError) {
                            isJson = false;
                          }
                        } else {
                          isJson = false;
                        }
                      }
                    }
                    if (!isJson && typeof message.content === 'object') {
                      jsonString = JSON.stringify(message.content, null, 2);
                      isJson = true;
                    }
                    if (isJson) {
                      return <JsonBlockWithCopy jsonString={jsonString} />;
                    }
                    // Sinon, markdown classique
                    return (
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          ...MarkdownComponents,
                          p: ({children}) => <p style={{marginBottom: '1em', lineHeight: '1.6'}}>{children}</p>
                        }}
                      >
                        {typeof message.content === 'string' 
                          ? message.content
                              .replace(/\r\n/g, '\n')
                              .replace(/\r/g, '\n')
                              .replace(/\n{3,}/g, '\n\n') // Limiter les sauts de lignes excessifs
                          : JSON.stringify(message.content, null, 2)
                        }
                      </ReactMarkdown>
                    );
                  })()
                ) : (
                  <span className="user-bubble">{message.content}</span>
                )
              )}
              {message.files && Array.isArray(message.files) && message.files.length > 0 && (
                <div className="message-files">
                  {message.files.map((file, idx) => {
                    if (file.type && file.type.startsWith('image/')) {
                      let preview = null;
                      if (message.role === 'user' && index === messages.findIndex(m => m.id === message.id)) {
                        preview = filePreviews[idx];
                      }
                      return (
                        <span key={idx} className="message-file-preview">
                          {preview ? (
                            <img src={preview} alt={file.name} className="message-img-preview" style={{maxWidth:120, maxHeight:120, borderRadius:8, marginRight:8}} />
                          ) : (
                            <span className="message-file-name">{file.name}</span>
                          )}
                        </span>
                      );
                    } else {
                      return <span key={idx} className="message-file-name">{file.name}</span>;
                    }
                  })}
                </div>
              )}
              {message.role === 'assistant' && message.content && (
                <div className="message-actions">
                  <button className="message-action-btn" title={t('chat.copy')} onClick={() => copyToClipboard(message.content, message.id)}>
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="7" y="3" width="10" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                      <rect x="3" y="7" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </button>
                  <button className="message-action-btn" title={t('chat.regenerate')} onClick={() => regenerateMessage(index)}>
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 10a6 6 0 0 1 10.39-4.19" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                      <path d="M16 4v4h-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                      <path d="M16 10a6 6 0 0 1-10.39 4.19" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                      <path d="M4 16v-4h4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                    </svg>
                  </button>
                  <button className="message-action-btn" title={t('chat.share')} onClick={() => shareMessage(message.content, message.id)}>
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="15" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                      <circle cx="5" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                      <circle cx="15" cy="15" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                      <path d="M7.2 11.2l5.6 2.6M12.8 6.2L7.2 8.8" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        ))
      )}
      {isLoading && (
        <div className="message assistant">
          <div className="message-avatar">
            <div className="ai-avatar">AI</div>
          </div>
          <div className="message-content">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}
export default MessagesList;
